import FirebaseService from '../services/firebaseService';

// Define a type for the dot objects
interface DotData {
  x: number;
  y: number;
  timestamp?: number;
  intensity?: number;
  distortionRadius?: number;
}

/**
 * Downloads an Amsler Grid test result image
 */
export const downloadTestResultImage = (testData: any): void => {
  try {
    // Generate file name based on test data
    const date = new Date(testData.timestamp);
    const formattedDate = date.toISOString().split('T')[0];
    const eyeType = testData.eyeTested === 'left' ? 'Left Eye' : 'Right Eye';
    const fileName = `AmslerGrid-${eyeType}-${formattedDate}.png`;
    
    // Get the Firebase service instance
    const firebaseService = FirebaseService.getInstance();
    
    // Generate image from test data
    const gridData = {
      id: 'temp-id',
      userId: 'temp-user',
      timestamp: testData.timestamp,
      eyeTested: testData.eyeTested,
      // Ensure dots have the required timestamp property
      dots: Array.isArray(testData.dots) ? testData.dots.map((dot: DotData) => {
        // Create a new dot object with required fields
        const newDot: DotData = {
          x: dot.x,
          y: dot.y,
          timestamp: dot.timestamp || testData.timestamp
        };
        
        // Add optional fields if they exist
        if (dot.intensity !== undefined) {
          newDot.intensity = dot.intensity;
        }
        
        if (dot.distortionRadius !== undefined) {
          newDot.distortionRadius = dot.distortionRadius;
        }
        
        return newDot;
      }) : [],
      abnormalities: testData.abnormalities || [],
      notes: testData.notes || ''
    };
    
    const imageDataUrl = firebaseService.generateGridImage(gridData);
    
    // Create a download link
    const a = document.createElement('a');
    a.href = imageDataUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (error) {
    console.error("Error downloading test result image:", error);
  }
};

/**
 * Shares an Amsler Grid test result
 */
export const shareTestResult = async (testData: any): Promise<void> => {
  try {
    // Get the Firebase service instance
    const firebaseService = FirebaseService.getInstance();
    
    // Generate image from test data
    const gridData = {
      id: 'temp-id',
      userId: 'temp-user',
      timestamp: testData.timestamp,
      eyeTested: testData.eyeTested,
      // Ensure dots have the required timestamp property
      dots: Array.isArray(testData.dots) ? testData.dots.map((dot: DotData) => {
        // Create a new dot object with required fields
        const newDot: DotData = {
          x: dot.x,
          y: dot.y,
          timestamp: dot.timestamp || testData.timestamp
        };
        
        // Add optional fields if they exist
        if (dot.intensity !== undefined) {
          newDot.intensity = dot.intensity;
        }
        
        if (dot.distortionRadius !== undefined) {
          newDot.distortionRadius = dot.distortionRadius;
        }
        
        return newDot;
      }) : [],
      abnormalities: testData.abnormalities || [],
      notes: testData.notes || ''
    };
    
    const imageDataUrl = firebaseService.generateGridImage(gridData);
    
    // Prepare share text
    const date = new Date(testData.timestamp);
    const formattedDate = date.toLocaleDateString();
    const eyeType = testData.eyeTested === 'left' ? 'Left Eye' : 'Right Eye';
    const abnormalitiesText = testData.abnormalities && testData.abnormalities.length > 0
      ? `Abnormalities: ${testData.abnormalities.join(', ')}`
      : 'No abnormalities detected';
    
    const shareText = `Amsler Grid Test Result\nDate: ${formattedDate}\nEye: ${eyeType}\n${abnormalitiesText}`;
    
    // Check if Web Share API is available
    if (navigator.share) {
      try {
        // Convert data URL to a File object for sharing
        const response = await fetch(imageDataUrl);
        const blob = await response.blob();
        const file = new File([blob], `amsler-grid-${eyeType.toLowerCase().replace(' ', '-')}-${date.toISOString().split('T')[0]}.png`, { type: 'image/png' });
        
        const shareData: any = {
          title: 'Amsler Grid Test Result',
          text: shareText
        };
        
        // Try to share with file if supported
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          shareData.files = [file];
        }
        
        await navigator.share(shareData);
      } catch (err) {
        console.error('Error sharing via Web Share API:', err);
        // Fallback to clipboard copy
        copyToClipboard(shareText);
        alert('Share information copied to clipboard');
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard(shareText);
      alert('Share information copied to clipboard');
    }
  } catch (error) {
    console.error("Error sharing test result:", error);
  }
};

/**
 * Prints an Amsler Grid test result
 */
export const printTestResult = (testData: any): void => {
  try {
    // Get the Firebase service instance
    const firebaseService = FirebaseService.getInstance();
    
    // Generate image from test data
    const gridData = {
      id: 'temp-id',
      userId: 'temp-user',
      timestamp: testData.timestamp,
      eyeTested: testData.eyeTested,
      // Ensure dots have the required timestamp property
      dots: Array.isArray(testData.dots) ? testData.dots.map((dot: DotData) => {
        // Create a new dot object with required fields
        const newDot: DotData = {
          x: dot.x,
          y: dot.y,
          timestamp: dot.timestamp || testData.timestamp
        };
        
        // Add optional fields if they exist
        if (dot.intensity !== undefined) {
          newDot.intensity = dot.intensity;
        }
        
        if (dot.distortionRadius !== undefined) {
          newDot.distortionRadius = dot.distortionRadius;
        }
        
        return newDot;
      }) : [],
      abnormalities: testData.abnormalities || [],
      notes: testData.notes || ''
    };
    
    const imageDataUrl = firebaseService.generateGridImage(gridData);
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print the test result');
      return;
    }
    
    // Prepare date and eye information
    const date = new Date(testData.timestamp);
    const formattedDate = date.toLocaleDateString();
    const formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const eyeType = testData.eyeTested === 'left' ? 'Left Eye' : 'Right Eye';
    
    // Create HTML content for the print window
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Amsler Grid Test Result</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .image-container {
              display: flex;
              justify-content: center;
              margin-bottom: 20px;
            }
            .image {
              max-width: 100%;
              height: auto;
              border: 1px solid #ddd;
            }
            .details {
              max-width: 600px;
              margin: 0 auto;
            }
            .detail-item {
              margin-bottom: 10px;
            }
            .label {
              font-weight: bold;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Amsler Grid Test Result</h1>
            <p>${formattedDate} at ${formattedTime}</p>
          </div>
          <div class="image-container">
            <img src="${imageDataUrl}" alt="Amsler Grid Test Result" class="image" />
          </div>
          <div class="details">
            <div class="detail-item">
              <span class="label">Date:</span> ${formattedDate} at ${formattedTime}
            </div>
            <div class="detail-item">
              <span class="label">Eye Tested:</span> ${eyeType}
            </div>
            <div class="detail-item">
              <span class="label">Abnormalities:</span> ${testData.abnormalities && testData.abnormalities.length > 0 ? testData.abnormalities.join(', ') : 'None detected'}
            </div>
            ${testData.notes ? `
              <div class="detail-item">
                <span class="label">Notes:</span> ${testData.notes}
              </div>
            ` : ''}
          </div>
          <script>
            // Automatically print when the page loads
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  } catch (error) {
    console.error("Error printing test result:", error);
  }
};

/**
 * Helper function to copy text to clipboard
 */
const copyToClipboard = (text: string): void => {
  // Create a temporary textarea element
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  
  // Select and copy the text
  textarea.select();
  document.execCommand('copy');
  
  // Remove the temporary element
  document.body.removeChild(textarea);
}; 